import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSocketEvent } from '@/hooks/useSocket'
import { useAuthStore } from '@/stores/authStore'
import { Toast } from '@/components/common/Toast'
import type { ToastVariant } from '@/components/common/Toast'

interface DataChangedPayload {
  entity: string
  action?: string
  title?: string
  projectName?: string
  actorId?: string
  actorName?: string
}

interface ToastItem {
  id: string
  title: string
  description: string
  variant: ToastVariant
  navigateTo?: string
}

const ENTITY_LABELS: Record<string, { label: string; icon: string; route: string }> = {
  tasks:    { label: 'Task', icon: 'task', route: '/tasks' },
  projects: { label: 'Progetto', icon: 'project', route: '/projects' },
  ped:      { label: 'Contenuto PED', icon: 'ped', route: '/ped' },
  quotes:   { label: 'Preventivo', icon: 'quote', route: '/preventivi' },
  meetings: { label: 'Meeting', icon: 'meeting', route: '/meetings' },
  clients:  { label: 'Cliente', icon: 'client', route: '/clients' },
}

export function RealtimeToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const userId = useAuthStore((s) => s.user?.id)
  const navigate = useNavigate()

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useSocketEvent<DataChangedPayload>('data:changed', (payload) => {
    // Solo creazioni, e non mostrare se l'azione l'ho fatta io
    if (payload.action !== 'created') return
    if (payload.actorId === userId) return
    if (!payload.title) return

    const entityConfig = ENTITY_LABELS[payload.entity]
    if (!entityConfig) return

    const projectSuffix = payload.projectName ? ` — ${payload.projectName}` : ''
    const actorPrefix = payload.actorName ? `${payload.actorName} ha aggiunto` : 'Aggiunto'

    const toast: ToastItem = {
      id: `${Date.now()}-${Math.random()}`,
      title: `Nuovo ${entityConfig.label}`,
      description: `${actorPrefix}: "${payload.title}"${projectSuffix}`,
      variant: 'info',
      navigateTo: entityConfig.route,
    }

    setToasts((prev) => [...prev.slice(-4), toast]) // max 5 toast visibili
  })

  return (
    <>
      {toasts.map((t) => (
        <Toast
          key={t.id}
          open={true}
          onOpenChange={(open) => {
            if (!open) removeToast(t.id)
          }}
          title={t.title}
          description={t.description}
          variant={t.variant}
          duration={5000}
        />
      ))}
    </>
  )
}
